import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  Grid,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AddPayrollPeriodDialog from "components/AddPayrollPeriodDialog";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import { useMaterialUIController } from "context";
import { useAppTheme } from "context/themeContext";
import {
  getPayrollPeriods,
  createPayrollPeriod,
  updatePayrollPeriod,
  deletePayrollPeriod,
} from "services/api";

const statusColors = {
  open: "success",
  processing: "info",
  closed: "warning",
  locked: "default",
};

const statusOptions = ["open", "processing", "closed", "locked"];

const PeriodListView = ({ canEdit, onSelectPeriod }) => {
  const { t } = useTranslation();
  const { primaryColor } = useAppTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const snackbarRef = useRef();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);

  useEffect(() => {
    const fetchPeriods = async () => {
      setLoading(true);
      const res = await getPayrollPeriods();
      if (res !== "error") {
        setPeriods(res);
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.payroll.periodsAndRuns.list.loadError"),
          "error"
        );
      }
      setLoading(false);
    };
    fetchPeriods();
  }, []);

  const filteredPeriods = periods.filter((p) => {
    const matchesSearch =
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.countryCode?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleOpenAdd = () => {
    setSelectedPeriod(null);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (period) => {
    setSelectedPeriod(period);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSubmit = async (form) => {
    if (editMode) {
      const res = await updatePayrollPeriod(selectedPeriod.id, form);
      if (res !== "error") {
        setPeriods(periods.map((p) => (p.id === res.id ? res : p)));
        snackbarRef.current?.showSnackbar(
          t("admin.payroll.periodsAndRuns.list.updateSuccess"),
          "success"
        );
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.payroll.periodsAndRuns.list.updateError"),
          "error"
        );
      }
    } else {
      const res = await createPayrollPeriod(form);
      if (res !== "error") {
        setPeriods([...periods, res]);
        snackbarRef.current?.showSnackbar(
          t("admin.payroll.periodsAndRuns.list.addSuccess"),
          "success"
        );
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.periodsAndRuns.list.addError"), "error");
      }
    }
    setDialogOpen(false);
  };

  const handleDeleteClick = () => {
    setDialogOpen(false);
    setPeriodToDelete(selectedPeriod);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const res = await deletePayrollPeriod(periodToDelete.id);
    if (res === "success") {
      setPeriods(periods.filter((p) => p.id !== periodToDelete.id));
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.list.deleteSuccess"),
        "success"
      );
    } else if (typeof res === "string" && res !== "error") {
      // Backend guard: period isn't open, or already has data against it
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.list.cannotDelete"),
        "error"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.list.deleteError"),
        "error"
      );
    }
    setConfirmOpen(false);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <MDBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <MDTypography variant="subtitle1" fontWeight="bold">
          {t("admin.payroll.periodsAndRuns.list.count")} {periods.length}
        </MDTypography>
        <MDButton color="primary" onClick={handleOpenAdd} disabled={!canEdit}>
          {t("admin.payroll.periodsAndRuns.list.addTitle")}
        </MDButton>
      </MDBox>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={7}>
          <TextField
            label={t("admin.payroll.periodsAndRuns.list.search")}
            variant="outlined"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <Autocomplete
            options={statusOptions}
            getOptionLabel={(status) => t(`admin.payroll.periodsAndRuns.statuses.${status}`)}
            value={statusFilter}
            onChange={(event, newValue) => setStatusFilter(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.payroll.periodsAndRuns.list.filterByStatus")}
              />
            )}
          />
        </Grid>
      </Grid>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: darkMode ? "#333" : "#fff",
          border: darkMode ? "2px solid " + primaryColor : null,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
          mb: 4,
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("admin.payroll.periodsAndRuns.list.fields.name")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("admin.payroll.periodsAndRuns.list.fields.countryCode")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("admin.payroll.periodsAndRuns.list.fields.dates")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("admin.payroll.periodsAndRuns.list.fields.payDate")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                {t("admin.payroll.periodsAndRuns.list.fields.status")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                {t("admin.payroll.periodsAndRuns.list.open")}
              </TableCell>
            </TableRow>
            {filteredPeriods.map((period) => (
              <TableRow
                key={period.id}
                hover
                sx={{
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <TableCell
                  sx={{ cursor: canEdit ? "pointer" : "default" }}
                  onClick={() => canEdit && handleOpenEdit(period)}
                >
                  {period.name}
                </TableCell>
                <TableCell
                  sx={{ cursor: canEdit ? "pointer" : "default" }}
                  onClick={() => canEdit && handleOpenEdit(period)}
                >
                  {period.countryCode}
                </TableCell>
                <TableCell
                  sx={{ cursor: canEdit ? "pointer" : "default" }}
                  onClick={() => canEdit && handleOpenEdit(period)}
                >
                  {period.startDate} – {period.endDate}
                </TableCell>
                <TableCell
                  sx={{ cursor: canEdit ? "pointer" : "default" }}
                  onClick={() => canEdit && handleOpenEdit(period)}
                >
                  {period.payDate}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={t(`admin.payroll.periodsAndRuns.statuses.${period.status}`)}
                    color={statusColors[period.status] || "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => onSelectPeriod(period)}
                  >
                    {t("admin.payroll.periodsAndRuns.list.open")}
                  </MDButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AddPayrollPeriodDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        period={selectedPeriod}
        editMode={editMode}
        handleDelete={handleDeleteClick}
      />

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        message={t("admin.payroll.periodsAndRuns.list.confirmDelete", {
          name: periodToDelete?.name,
        })}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </>
  );
};

PeriodListView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
  onSelectPeriod: PropTypes.func.isRequired,
};

export default PeriodListView;
